import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type PillStatus =
  | "pending"
  | "confirmed"
  | "operator_confirmed"
  | "in_progress"
  | "completed"
  | "cancelled_trekker"
  | "cancelled_operator"
  | "refunded"
  | "disputed"
  | "verified"
  | "pending_review"
  | "rejected"
  | "suspended"
  | "paid"
  | "processing"
  | "failed"
  | "open"
  | "full"
  | "filling";

export interface StatusPillProps {
  status: PillStatus;
  size?: "sm" | "md";
  showDot?: boolean;
  className?: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

interface StatusConfig {
  label: string;
  pill: string;
  dot: string;
  pulse: boolean;
}

const STATUS_CONFIG: Record<PillStatus, StatusConfig> = {
  pending: {
    label: "Pending",
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    dot: "bg-amber-500",
    pulse: false,
  },
  confirmed: {
    label: "Confirmed",
    pill: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    dot: "bg-blue-500",
    pulse: false,
  },
  operator_confirmed: {
    label: "Operator Confirmed",
    pill: "bg-blue-200 text-blue-900 dark:bg-blue-800/50 dark:text-blue-200",
    dot: "bg-blue-700 dark:bg-blue-400",
    pulse: false,
  },
  in_progress: {
    label: "In Progress",
    pill: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
    dot: "bg-brand-purple",
    pulse: true,
  },
  completed: {
    label: "Completed",
    pill: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    dot: "bg-green-500",
    pulse: false,
  },
  cancelled_trekker: {
    label: "Cancelled",
    pill: "bg-slate-100 text-slate-600 dark:bg-surface-overlay dark:text-content-secondary",
    dot: "bg-slate-400",
    pulse: false,
  },
  cancelled_operator: {
    label: "Cancelled by Operator",
    pill: "bg-slate-100 text-slate-600 dark:bg-surface-overlay dark:text-content-secondary",
    dot: "bg-slate-400",
    pulse: false,
  },
  refunded: {
    label: "Refunded",
    pill: "bg-slate-100 text-slate-500 dark:bg-surface-overlay dark:text-content-secondary",
    dot: "bg-slate-400",
    pulse: false,
  },
  disputed: {
    label: "Disputed",
    pill: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    dot: "bg-red-500",
    pulse: false,
  },
  verified: {
    label: "Verified",
    pill: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    dot: "bg-green-500",
    pulse: false,
  },
  pending_review: {
    label: "Pending Review",
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    dot: "bg-amber-500",
    pulse: false,
  },
  rejected: {
    label: "Rejected",
    pill: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    dot: "bg-red-500",
    pulse: false,
  },
  suspended: {
    label: "Suspended",
    pill: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    dot: "bg-red-500",
    pulse: false,
  },
  paid: {
    label: "Paid",
    pill: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    dot: "bg-green-500",
    pulse: false,
  },
  processing: {
    label: "Processing",
    pill: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    dot: "bg-blue-500",
    pulse: true,
  },
  failed: {
    label: "Failed",
    pill: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    dot: "bg-red-500",
    pulse: false,
  },
  open: {
    label: "Open",
    pill: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    dot: "bg-green-500",
    pulse: false,
  },
  full: {
    label: "Full",
    pill: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    dot: "bg-red-500",
    pulse: false,
  },
  filling: {
    label: "Filling Fast",
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    dot: "bg-amber-500",
    pulse: true,
  },
};

const SIZE_CLASSES: Record<"sm" | "md", string> = {
  sm: "text-2xs px-1.5 py-0.5 gap-1",
  md: "text-xs px-2 py-1 gap-1.5",
};

const DOT_SIZE: Record<"sm" | "md", string> = {
  sm: "w-1.5 h-1.5",
  md: "w-2 h-2",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function StatusPill({
  status,
  size = "md",
  showDot = false,
  className,
}: StatusPillProps) {
  const config = STATUS_CONFIG[status];
  const shouldPulse = showDot && config.pulse;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium whitespace-nowrap",
        config.pill,
        SIZE_CLASSES[size],
        className,
      )}
    >
      {showDot && (
        <span className={cn("relative inline-flex shrink-0 rounded-full", DOT_SIZE[size])}>
          {shouldPulse && (
            <span
              className={cn(
                "absolute inset-0 rounded-full animate-ping opacity-75",
                config.dot,
              )}
            />
          )}
          <span className={cn("relative rounded-full", DOT_SIZE[size], config.dot)} />
        </span>
      )}
      {config.label}
    </span>
  );
}

StatusPill.displayName = "StatusPill";
